import { useEffect, useState, useCallback } from 'react';
import { useFiltersStore, useMapViewStore, useTextSearchStore } from '../stores';
import ShowrunnerMap from '../components/ShowRunnerMap';
import { trendingShowsLayer, regularShowsLayer } from '../util/data-factory';
import { useRouter } from '../util/router';
import { getShows } from '../util/util';

const EmbedMap = () => {
  const { params } = useRouter();
  const [featureData, setFeatureData] = useState();
  const [shows, setShows] = useState([]);
  const [error, setError] = useState(null);

  const {
    open,
    showType,
    selectedMediums,
    selectedLocation,
    selectedSubject,
    selectedStyles,
    sortedByRelevance,
  } = useFiltersStore();

  const { query } = useTextSearchStore();
  const { mainMapViewState, setMainMapViewState } = useMapViewStore();

  const [mapPopupInfo, setMapPopupInfo] = useState();
  const [mapImageInfo, setMapImageInfo] = useState();
  const [mapTopTagsInfo, setMapTopTagsInfo] = useState();

  // Fetch shows based on URL parameters or current filters
  const fetchShows = useCallback(async () => {
    try {
      // Parse URL parameters for filters if provided
      const urlParams = new URLSearchParams(window.location.search);
      const filtersFromUrl = {
        open: urlParams.get('open')?.split(',').filter(Boolean) || [],
        sortedByRelevance: urlParams.get('sortedByRelevance') === 'true',
        selectedMediums: urlParams.get('mediums')?.split(',').filter(Boolean) || [],
        selectedLocation: urlParams.get('locations')?.split(',').filter(Boolean) || [],
        selectedSubject: urlParams.get('subjects')?.split(',').filter(Boolean) || [],
        selectedStyles: urlParams.get('styles')?.split(',').filter(Boolean) || [],
        textSearch: urlParams.get('query') || '',
        showType: urlParams.get('types')?.split(',').filter(Boolean) || [],
        fairName: params.fairname || urlParams.get('fairName'),
        city: urlParams.get('city') || 'New York',
      };

      const data = await getShows({
        open: filtersFromUrl.open.length ? new Set(filtersFromUrl.open) : open,
        sortedByRelevance:
          filtersFromUrl.sortedByRelevance !== undefined
            ? filtersFromUrl.sortedByRelevance
            : sortedByRelevance,
        selectedMediums: filtersFromUrl.selectedMediums.length
          ? new Set(filtersFromUrl.selectedMediums)
          : selectedMediums,
        selectedLocation: filtersFromUrl.selectedLocation.length
          ? new Set(filtersFromUrl.selectedLocation)
          : selectedLocation,
        selectedSubject: filtersFromUrl.selectedSubject.length
          ? new Set(filtersFromUrl.selectedSubject)
          : selectedSubject,
        selectedStyles: filtersFromUrl.selectedStyles.length
          ? new Set(filtersFromUrl.selectedStyles)
          : selectedStyles,
        textSearch: filtersFromUrl.textSearch || query,
        showType: filtersFromUrl.showType.length ? new Set(filtersFromUrl.showType) : showType,
        fairName: filtersFromUrl.fairName,
        city: filtersFromUrl.city,
      });

      setShows(data);
    } catch (error) {
      console.error(error);
      setError('Failed to fetch shows');
    }
  }, [
    open,
    sortedByRelevance,
    selectedLocation,
    selectedMediums,
    selectedSubject,
    selectedStyles,
    showType,
    params.fairname,
    query,
  ]);

  useEffect(() => {
    fetchShows();
  }, [fetchShows]);

  // Format filtered data as features for the map
  useEffect(() => {
    const formatted_data = shows.map(i => {
      let longitude = parseFloat(i.venues.longitude);
      let latitude = parseFloat(i.venues.latitude);
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
        properties: { ...i, lat: latitude, lng: longitude },
      };
    });

    setFeatureData({ type: 'FeatureCollection', features: formatted_data });
  }, [shows]);

  const onPinClick = useCallback((event, mapRef) => {
    const feature = event.features && event.features[0];
    if (!feature) return;
    const layer = feature.layer;

    if (layer.id === trendingShowsLayer.id || layer.id === regularShowsLayer.id) {
      event.originalEvent.stopPropagation();
      setMapPopupInfo({ ...feature.properties, venues: JSON.parse(feature.properties.venues) });
      setMapImageInfo(JSON.parse(feature.properties.image_urls));
      setMapTopTagsInfo(JSON.parse(feature.properties.top_tags));
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [feature.properties.lng, feature.properties.lat],
          essential: true,
          curve: 1.5,
          duration: 1000,
          padding: {
            top: 0,
            bottom: 300,
            left: 150,
            right: 0,
          },
        });
      }
    } else {
      event.originalEvent.stopPropagation();
    }
  }, []);

  const onMove = useCallback(
    event => {
      const { latitude, longitude, zoom } = event.viewState;
      setMainMapViewState({ latitude, longitude, zoom });
    },
    [setMainMapViewState]
  );

  const closeShowMapDialog = () => {
    setMapPopupInfo(null);
    setMapImageInfo(null);
    setMapTopTagsInfo(null);
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ width: '100vw', height: '100vh', margin: 0, padding: 0 }}>
      <ShowrunnerMap
        mainMapViewState={mainMapViewState}
        featureData={featureData}
        mapImageInfo={mapImageInfo}
        mapPopupInfo={mapPopupInfo}
        mapTopTagsInfo={mapTopTagsInfo}
        onPinClick={onPinClick}
        onMove={onMove}
        closeShowMapDialog={closeShowMapDialog}
        isEmbed={true}
      />
    </div>
  );
};

export default EmbedMap;
